import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { City } from "@/lib/city";

/**
 * Typed bindings for the Phase E2 alert API (doc 22 §3 contract).
 *
 * The 6 GP routes + 3 moderator routes live under:
 *   - /:citySlug/v1/alerts                          (GP, city-scoped)
 *   - /:citySlug/v1/alerts/:id
 *   - /:citySlug/v1/alerts/:id/read                 POST
 *   - /:citySlug/v1/alerts/:id/action               POST
 *   - /:citySlug/v1/alerts/subscriptions            GET + POST
 *   - /:citySlug/v1/alerts/settings/digest-time     GET + POST
 *   - /v1/moderator/alert-candidates                GET  (mod-only, cross-city)
 *   - /v1/moderator/alert-candidates/:id/publish    POST
 *   - /v1/moderator/alert-candidates/:id/reject     POST
 *
 * All list responses use keyset cursor pagination; cursor is the
 * `published_at` of the last item on the previous page.
 */

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

export type AlertCategory =
  | "infectious_disease"
  | "immunisation"
  | "medication"
  | "public_health"
  | "recall"
  | "clinical_guideline"
  | "workforce"
  | "other";

export type AlertSeverity = "info" | "watch" | "advisory" | "warning" | "critical";

export interface AlertListItem {
  id: string;
  title: string;
  summary: string | null;
  category: AlertCategory;
  severity: AlertSeverity;
  published_at: string;
  source_url: string | null;
  tags: string[] | null;
  city_id: string | null;
  region_tags: string[];
  read_at: string | null;
}

export interface AlertDetail extends AlertListItem {
  body_markdown: string;
  valid_until: string | null;
  linked_tool_ids: string[];
  linked_thread_id: string | null;
  actioned_at: string | null;
}

export interface AlertListResponse {
  items: AlertListItem[];
  nextCursor: string | null;
}

export interface AlertSubscription {
  id: string;
  gp_id: string;
  categories: AlertCategory[];
  sources: string[] | null;
  delivery: Array<"email" | "sms" | "inapp" | "push">;
  daily_digest: boolean;
  created_at: string;
  updated_at: string;
}

export interface DigestSettings {
  digest_time_local: string;
  quiet_start_local: string;
  quiet_end_local: string;
  unsubscribed_digests: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  defaulted?: boolean;
}

export interface AlertFilters {
  category?: AlertCategory;
  severity?: AlertSeverity;
  unreadOnly?: boolean;
  cursor?: string;
  limit?: number;
  enabled?: boolean;
}

// ──────────────────────────────────────────────
// Query keys — shared so mutations can invalidate precisely
// ──────────────────────────────────────────────

export const alertQueryKeys = {
  all: (citySlug: string) => ["alerts", citySlug] as const,
  list: (citySlug: string, filters: AlertFilters) =>
    ["alerts", citySlug, "list", filters] as const,
  detail: (citySlug: string, id: string) =>
    ["alerts", citySlug, "detail", id] as const,
  subscriptions: (citySlug: string) =>
    ["alerts", citySlug, "subscriptions"] as const,
  digest: (citySlug: string) => ["alerts", citySlug, "digest"] as const,
};

// ──────────────────────────────────────────────
// Queries
// ──────────────────────────────────────────────

export function useAlerts(city: City, filters: AlertFilters = {}) {
  return useQuery({
    queryKey: alertQueryKeys.list(city.slug, filters),
    queryFn: () =>
      api.get<AlertListResponse>(`/${city.slug}/v1/alerts`, {
        params: {
          category: filters.category,
          severity: filters.severity,
          unreadOnly: filters.unreadOnly,
          cursor: filters.cursor,
          limit: filters.limit,
        },
      }),
    enabled: filters.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useAlertDetail(city: City, id: string | undefined) {
  return useQuery({
    queryKey: id ? alertQueryKeys.detail(city.slug, id) : ["alerts", "disabled"],
    queryFn: () =>
      api.get<AlertDetail>(`/${city.slug}/v1/alerts/${id!}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useAlertSubscriptions(city: City) {
  return useQuery({
    queryKey: alertQueryKeys.subscriptions(city.slug),
    queryFn: () =>
      api.get<{ items: AlertSubscription[] }>(
        `/${city.slug}/v1/alerts/subscriptions`,
      ),
    staleTime: 60_000,
  });
}

export function useDigestSettings(city: City) {
  return useQuery({
    queryKey: alertQueryKeys.digest(city.slug),
    queryFn: () =>
      api.get<DigestSettings>(
        `/${city.slug}/v1/alerts/settings/digest-time`,
      ),
    staleTime: 60_000,
  });
}

// ──────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────

/**
 * Optimistic read: the UI greys out the alert the instant the user opens
 * it, then the POST /read hits the server. On 409-ish "already_read" from
 * the server we don't need to do anything — the optimistic state matches.
 */
export function useMarkAlertRead(city: City) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ status: "read" | "already_read" }>(
        `/${city.slug}/v1/alerts/${id}/read`,
      ),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: alertQueryKeys.all(city.slug) });
      const nowIso = new Date().toISOString();
      // Patch list cache — every query whose key starts with our prefix.
      qc.setQueriesData<AlertListResponse | undefined>(
        { queryKey: ["alerts", city.slug, "list"] },
        (data) =>
          data
            ? {
                ...data,
                items: data.items.map((a) =>
                  a.id === id && !a.read_at ? { ...a, read_at: nowIso } : a,
                ),
              }
            : data,
      );
      // Patch detail cache.
      qc.setQueryData<AlertDetail | undefined>(
        alertQueryKeys.detail(city.slug, id),
        (d) => (d ? { ...d, read_at: d.read_at ?? nowIso } : d),
      );
      return { id };
    },
    onError: (_err, _id) => {
      // Roll back by refetching — our optimistic shape isn't worth the
      // complexity of exact rollback; server state is cheap to pull.
      qc.invalidateQueries({ queryKey: alertQueryKeys.all(city.slug) });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: alertQueryKeys.all(city.slug) });
    },
  });
}

export function useActionAlert(city: City) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<{ read_at: string; actioned_at: string }>(
        `/${city.slug}/v1/alerts/${id}/action`,
        { note },
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: alertQueryKeys.detail(city.slug, id) });
      qc.invalidateQueries({ queryKey: ["alerts", city.slug, "list"] });
    },
  });
}

export function useUpsertSubscription(city: City) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Pick<AlertSubscription, "categories" | "sources" | "delivery" | "daily_digest">>) =>
      api.post<AlertSubscription>(`/${city.slug}/v1/alerts/subscriptions`, {
        categories: body.categories,
        sources: body.sources,
        delivery: body.delivery,
        dailyDigest: body.daily_digest,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: alertQueryKeys.subscriptions(city.slug) });
    },
  });
}

export function useUpsertDigestSettings(city: City) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Partial<
        Pick<
          DigestSettings,
          | "digest_time_local"
          | "quiet_start_local"
          | "quiet_end_local"
          | "unsubscribed_digests"
          | "email_enabled"
          | "sms_enabled"
        >
      >,
    ) =>
      api.post<DigestSettings>(
        `/${city.slug}/v1/alerts/settings/digest-time`,
        {
          digestTimeLocal: body.digest_time_local,
          quietStartLocal: body.quiet_start_local,
          quietEndLocal: body.quiet_end_local,
          unsubscribedDigests: body.unsubscribed_digests,
          emailEnabled: body.email_enabled,
          smsEnabled: body.sms_enabled,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: alertQueryKeys.digest(city.slug) });
    },
  });
}

// ──────────────────────────────────────────────
// Utilities — shared across feed + detail components
// ──────────────────────────────────────────────

export const ALERT_CATEGORY_LABELS: Record<AlertCategory, string> = {
  infectious_disease: "Infectious disease",
  immunisation: "Immunisation",
  medication: "Medication",
  public_health: "Public health",
  recall: "Recall",
  clinical_guideline: "Clinical guideline",
  workforce: "Workforce",
  other: "Other",
};

/** Labels on the public 4-step scale (see ALERT_LEVELS in public-alerts.ts). */
export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info: "Update",
  watch: "Advisory",
  advisory: "Advisory",
  warning: "Warning",
  critical: "Urgent",
};

/**
 * Severity → design-token mapping. Maps to CSS variables already defined
 * in index.css (Sea-Glass Teal palette). Critical uses the accent colour
 * `#F27E62` (reserved for urgency, doc 24 Candidate A).
 */
export function severityBadgeClass(severity: AlertSeverity): string {
  switch (severity) {
    case "critical":
      return "bg-pb-accent/15 text-pb-accent border-pb-accent/30";
    case "warning":
      return "bg-amber-100 text-amber-900 border-amber-200";
    case "advisory":
      return "bg-pb-primary/10 text-pb-primary border-pb-primary/20";
    case "watch":
      return "bg-sky-100 text-sky-900 border-sky-200";
    case "info":
    default:
      return "bg-pb-ink/5 text-pb-ink-2 border-pb-border";
  }
}

export function isEndpointMissing(err: unknown): boolean {
  return (
    (err instanceof ApiError && err.status === 404 && err.code === "common.not_found") ||
    err instanceof TypeError // network failure before hitting the server
  );
}

export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
}
